import { datadogLogs } from "@datadog/browser-logs";
import { isDevEnvironment } from "../../utils/general";

/**
 * Configuration options for Datadog Browser Logs
 */
export interface DatadogLogsConfig {
  /** Datadog client token (same as RUM, public token) */
  clientToken: string;
  /** Service name (e.g., 'champions-frontend', 'learn-frontend') */
  service: string;
  /** Version/build identifier (e.g., git SHA) for tracking deployments */
  version?: string;
  /** Forward console errors to Datadog (default: true) */
  forwardErrorsToLogs?: boolean;
  /** Forward console logs to Datadog (default: false - reduces noise) */
  forwardConsoleLogs?: ("log" | "debug" | "info" | "warn" | "error")[];
  /** Sample rate for logs (0-100, default: 100 = all logs) */
  sessionSampleRate?: number;
}

/**
 * Initialize Datadog Browser Logs
 *
 * Forwards frontend logs (console errors, custom logs) to Datadog for centralized logging.
 * Integrates with RUM for correlated debugging (logs + sessions + errors).
 *
 * **Local Development:** Automatically disabled when running locally (pnpm start / pnpm run atlas)
 * to prevent telemetry pollution from developer machines. Only initializes in CI/CD builds where
 * VITE_APP_ENVIRONMENT is explicitly set to "development" or "production".
 *
 * Features:
 * - Automatic console error forwarding
 * - Optional console log forwarding (log, debug, info, warn)
 * - Correlation with RUM sessions
 * - Custom log attributes and context
 *
 * @param config - Datadog logs configuration options
 *
 * @example
 * ```typescript
 * initDatadogLogs({
 *   clientToken: "pub456",
 *   service: "my-frontend",
 *   version: import.meta.env.VITE_APP_GIT_SHA,
 * });
 *
 * // Then use like this:
 * import { datadogLogs } from '@datadog/browser-logs';
 * datadogLogs.logger.info('User logged in', { userId: '123' });
 * datadogLogs.logger.error('API call failed', { endpoint: '/api/users', error });
 * ```
 */
export const initDatadogLogs = ({
  clientToken,
  service,
  version,
  forwardErrorsToLogs = true,
  forwardConsoleLogs = ["error"], // By default, only forward errors; specify other console methods to forward more (e.g., ["log", "warn", "error"])
  sessionSampleRate = 100,
}: DatadogLogsConfig) => {
  // Disable Datadog in local development mode
  // VITE_APP_ENVIRONMENT is only set in CI/CD builds (via set_dev_env.sh / set_prod_env.sh)
  // When running locally (pnpm start / pnpm run atlas), it's undefined
  const environment = import.meta.env.VITE_APP_ENVIRONMENT;
  if (!environment || environment === "local") {
    console.info("ℹ️ Datadog Logs disabled (local development mode)");
    return;
  }

  datadogLogs.init({
    clientToken,
    site: "datadoghq.com",
    service,
    env: isDevEnvironment() ? "development" : "production",
    version,
    forwardErrorsToLogs, // Automatically forward console.error() to Datadog
    forwardConsoleLogs, // Forward specified console methods
    sessionSampleRate, // Sample 100% of sessions for logs
  });
};

/**
 * Example usage in application code:
 *
 * // Log user actions
 * datadogLogs.logger.info('User completed assessment', {
 *   assessmentId: '123',
 *   score: 85,
 *   duration: 300,
 * });
 *
 * // Log API errors with context
 * try {
 *   const data = await fetch('/api/endpoint');
 * } catch (error) {
 *   datadogLogs.logger.error('API request failed', {
 *     endpoint: '/api/endpoint',
 *     error: error.message,
 *     stack: error.stack,
 *   });
 * }
 *
 * // Add global context (set once, included in all logs)
 * datadogLogs.addLoggerGlobalContext('user', {
 *   id: userId,
 *   email: userEmail,
 *   role: userRole,
 * });
 */
